import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/components/common/SafeIcon';
import { useAuthContext } from '@/components/auth/AuthProvider';

const { FiChevronLeft, FiCheck, FiStar, FiZap, FiGift, FiX } = FiIcons;

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [activeTab, setActiveTab] = useState('subscription');

  // 🆕 全新定價方案 (Open Router 免費版)
  const subscriptionPlans = [
    {
      id: 'free',
      name: '散客',
      nameEn: 'Wanderer',
      price: { monthly: 0, yearly: 0 },
      features: [
        { icon: FiCheck, text: '每晚 20 杯飲料 (訊息)', highlight: true },
        { icon: FiCheck, text: '每晚 3 次單張占卜', highlight: true },
      ],
      limitations: [
        '無法使用三張牌陣',
        '無深度解讀服務',
        '對話記錄不保存 (清除瀏覽器後消失)'
      ],
      current: true,
      cta: '當前身份',
      gradient: 'from-gray-600/20 to-gray-800/20',
      border: 'border-gray-600/30',
      badge: null,
    },
    {
      id: 'basic',
      name: '常客',
      nameEn: 'Regular',
      price: { monthly: 2.99, yearly: 29.99 },
      discount: { monthly: 0, yearly: 17 },
      badge: '超值推薦',
      features: [
        { icon: FiCheck, text: '每晚 100 杯飲料 (訊息)', highlight: true },
        { icon: FiCheck, text: '無限單張占卜', highlight: true },
        { icon: FiCheck, text: '每晚 10 次三張牌陣', highlight: true },
        { icon: FiCheck, text: '每月 10 次深度解讀', highlight: true },
        { icon: FiStar, text: '✨ 對話記錄雲端保存', highlight: true },
      ],
      cta: '成為常客',
      gradient: 'from-amber-600/20 to-amber-800/20',
      border: 'border-amber-500/40',
      popular: true,
    },
    {
      id: 'premium',
      name: 'VIP 會員',
      nameEn: 'VIP Member',
      price: { monthly: 4.99, yearly: 49.99 },
      discount: { monthly: 0, yearly: 17 },
      badge: '無限暢聊',
      features: [
        { icon: FiStar, text: '無限制訊息 (暢聊到天亮)', highlight: true },
        { icon: FiStar, text: '無限單張占卜', highlight: true },
        { icon: FiStar, text: '無限三張牌陣', highlight: true },
        { icon: FiStar, text: '每月 30 次深度解讀', highlight: true },
        { icon: FiStar, text: '✨ 對話記錄雲端保存', highlight: true },
        { icon: FiStar, text: '優先使用最新 AI 模型', highlight: true },
      ],
      cta: '升級 VIP',
      gradient: 'from-amber-500/30 to-amber-700/30',
      border: 'border-amber-400/60',
      popular: false,
    },
  ];

  // 🆕 全新單次購買價格
  const creditPackages = [
    {
      category: '飲料券',
      categoryEn: 'Message Credits',
      icon: FiZap,
      description: '用來點飲料聊天的代幣 (對話訊息)',
      items: [
        { amount: 100, price: 0.99, perUnit: 0.01, popular: false },
        { amount: 500, price: 3.99, perUnit: 0.008, popular: true },
        { amount: 2000, price: 9.99, perUnit: 0.005, popular: false, badge: '最划算' },
      ],
    },
    {
      category: '占卜券',
      categoryEn: 'Tarot Spread Credits',
      icon: FiGift,
      description: '用來請占卜師解牌的代幣 (三張牌陣)',
      items: [
        { amount: 10, price: 1.99, perUnit: 0.20, popular: false },
        { amount: 50, price: 7.99, perUnit: 0.16, popular: true },
        { amount: 200, price: 24.99, perUnit: 0.12, popular: false, badge: '最划算' },
      ],
    },
    {
      category: '深度解讀券',
      categoryEn: 'Deep Reading Credits',
      icon: FiStar,
      description: '用來請求專業深度解讀的代幣',
      items: [
        { amount: 5, price: 0.99, perUnit: 0.20, popular: false },
        { amount: 25, price: 6.99, perUnit: 0.28, popular: true },
        { amount: 100, price: 19.99, perUnit: 0.20, popular: false, badge: '最划算' },
      ],
    },
  ];

  const handleUpgrade = (planId) => {
    if (!user) {
      navigate('/login');
      return;
    }
    console.log('升級至:', planId, billingCycle);
  };

  const handleBuyCredits = (category, amount, price) => {
    if (!user) {
      navigate('/login');
      return;
    }
    console.log('購買:', category, amount, price);
  };

  const getPrice = (plan) => {
    const priceObj = plan.price || {};
    const price = priceObj[billingCycle];
    return typeof price === 'number' ? price : 0;
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#0a0a0c] flex flex-col overflow-y-auto no-scrollbar">
      <div className="grain-overlay" />
      <div className="vignette" />

      <header className="fixed top-0 left-0 right-0 z-50 p-6 md:p-10 safe-pt flex justify-between items-center bg-gradient-to-b from-[#0a0a0c]/95 to-transparent backdrop-blur-sm">
        <button
          onClick={() => navigate(-1)}
          className="group flex items-center space-x-2 text-gray-400 hover:text-amber-400 transition-colors pointer-events-auto"
        >
          <div className="w-10 h-10 rounded-full border border-white/10 group-hover:border-amber-400/50 flex items-center justify-center bg-white/[0.02] backdrop-blur-sm transition-all shadow-md">
            <SafeIcon icon={FiChevronLeft} className="text-base group-hover:-translate-x-0.5 transition-transform" />
          </div>
          <span className="text-[10px] tracking-[0.3em] uppercase hidden sm:block">Back</span>
        </button>

        <div className="pointer-events-auto group relative">
          <div className="flex flex-col items-end space-y-1.5 md:space-y-2">
            <span className="text-amber-500/80 font-serif tracking-[0.2em] md:tracking-[0.3em] text-xs md:text-sm whitespace-nowrap">
              Midnight Bar
            </span>
            <span className="text-gray-400 font-serif text-[8px] md:text-[9px] tracking-[0.4em] uppercase whitespace-nowrap">
              Membership Plans
            </span>
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-start justify-center px-6 pt-32 pb-20 safe-pb relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="w-full max-w-6xl"
        >
          <div className="text-center mb-12 md:mb-16">
            <motion.h1
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl md:text-4xl lg:text-5xl font-serif text-amber-400 tracking-widest mb-4"
            >
              選擇你的會員身份
            </motion.h1>
            <p className="text-gray-500 text-xs md:text-sm tracking-wider">
              Choose Your Midnight Membership
            </p>
            {/* 🆕 新增:免費 AI 提示 */}
            <div className="mt-6 inline-flex items-center space-x-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full">
              <span className="text-green-400 text-xs tracking-wider">✨ 使用免費 AI 模型,價格更親民</span>
            </div>
          </div>

          {/* 訂閱方案與單次購買切換 */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex bg-white/5 border border-white/10 rounded-full p-1 backdrop-blur-md">
              <button
                onClick={() => setActiveTab('subscription')}
                className={`px-6 py-2.5 rounded-full text-sm tracking-widest transition-all duration-300 ${
                  activeTab === 'subscription'
                    ? 'bg-amber-500 text-black'
                    : 'text-gray-400 hover:text-amber-400'
                }`}
              >
                會員方案
              </button>
              <button
                onClick={() => setActiveTab('credits')}
                className={`px-6 py-2.5 rounded-full text-sm tracking-widest transition-all duration-300 ${
                  activeTab === 'credits'
                    ? 'bg-amber-500 text-black'
                    : 'text-gray-400 hover:text-amber-400'
                }`}
              >
                單次充值
              </button>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {activeTab === 'subscription' && (
              <motion.div
                key="subscription"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.5 }}
              >
                {/* 月付/年付切換 */}
                <div className="flex justify-center mb-10">
                  <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-full p-1 backdrop-blur-md">
                    <button
                      onClick={() => setBillingCycle('monthly')}
                      className={`px-5 py-2 rounded-full text-xs tracking-widest transition-all duration-300 ${
                        billingCycle === 'monthly'
                          ? 'bg-white/10 text-amber-400'
                          : 'text-gray-500'
                      }`}
                    >
                      月付
                    </button>
                    <button
                      onClick={() => setBillingCycle('yearly')}
                      className={`px-5 py-2 rounded-full text-xs tracking-widest transition-all duration-300 relative ${
                        billingCycle === 'yearly'
                          ? 'bg-white/10 text-amber-400'
                          : 'text-gray-500'
                      }`}
                    >
                      年付
                      <span className="absolute -top-2 -right-2 bg-amber-500 text-black text-[8px] px-1.5 py-0.5 rounded-full">
                        省 17%
                      </span>
                    </button>
                  </div>
                </div>

                {/* 訂閱方案卡片 */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                  {subscriptionPlans.map((plan, index) => {
                    const price = getPrice(plan);
                    const isYearly = billingCycle === 'yearly';
                    const discount = isYearly && plan.discount ? plan.discount.yearly : 0;
                    const savings = isYearly && plan.price.monthly
                      ? (plan.price.monthly * 12 - price).toFixed(2)
                      : 0;

                    return (
                      <motion.div
                        key={plan.id}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1, duration: 0.6 }}
                        className={`relative bg-gradient-to-br ${plan.gradient} border ${plan.border} rounded-3xl p-6 md:p-8 backdrop-blur-md ${
                          plan.popular ? 'md:scale-105 shadow-[0_0_40px_rgba(245,158,11,0.2)]' : ''
                        }`}
                      >
                        {plan.badge && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-black text-[10px] px-4 py-1 rounded-full tracking-wider font-medium">
                            {plan.badge}
                          </div>
                        )}

                        <div className="text-center mb-6">
                          <h3 className="text-xl md:text-2xl font-serif text-amber-400 tracking-widest mb-1">
                            {plan.name}
                          </h3>
                          <p className="text-gray-500 text-[10px] tracking-wider uppercase">
                            {plan.nameEn}
                          </p>
                        </div>

                        <div className="text-center mb-8">
                          <div className="flex items-baseline justify-center space-x-2">
                            <span className="text-4xl md:text-5xl font-light text-white">
                              ${price.toFixed(2)}
                            </span>
                            {plan.id !== 'free' && (
                              <span className="text-gray-500 text-sm">
                                / {billingCycle === 'monthly' ? '月' : '年'}
                              </span>
                            )}
                          </div>
                          {isYearly && discount > 0 && savings > 0 && (
                            <p className="text-amber-400/60 text-xs mt-2">
                              省 ${savings}
                            </p>
                          )}
                        </div>

                        <ul className="space-y-3 mb-8">
                          {plan.features.map((feature, i) => (
                            <li key={i} className="flex items-center space-x-3 text-sm">
                              <SafeIcon
                                icon={feature.icon}
                                className={`flex-shrink-0 ${
                                  feature.highlight ? 'text-amber-400' : 'text-gray-500'
                                }`}
                              />
                              <span className={feature.highlight ? 'text-white' : 'text-gray-400'}>
                                {feature.text}
                              </span>
                            </li>
                          ))}
                          {plan.limitations?.map((limitation, i) => (
                            <li key={i} className="flex items-start space-x-3 text-sm text-gray-600">
                              <span className="w-4 h-4 flex-shrink-0 mt-0.5">
                                <SafeIcon icon={FiX} className="text-red-400/60" />
                              </span>
                              <span>{limitation}</span>
                            </li>
                          ))}
                        </ul>

                        <button
                          onClick={() => handleUpgrade(plan.id)}
                          disabled={plan.current}
                          className={`w-full py-3 rounded-xl text-sm tracking-widest transition-all duration-300 ${
                            plan.current
                              ? 'bg-white/5 text-gray-600 cursor-not-allowed'
                              : plan.popular
                              ? 'bg-amber-500 text-black hover:bg-amber-400 shadow-[0_0_20px_rgba(245,158,11,0.3)]'
                              : 'bg-white/10 text-amber-400 border border-amber-500/30 hover:bg-amber-500/10'
                          }`}
                        >
                          {plan.cta}
                        </button>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {activeTab === 'credits' && (
              <motion.div
                key="credits"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5 }}
                className="space-y-12"
              >
                {creditPackages.map((pkg, pkgIndex) => (
                  <div key={pkg.category}>
                    <div className="flex items-center space-x-3 mb-6">
                      <SafeIcon icon={pkg.icon} className="text-amber-400 text-xl" />
                      <div>
                        <h3 className="text-xl font-serif text-amber-400 tracking-widest">
                          {pkg.category}
                        </h3>
                        <p className="text-gray-500 text-[10px] tracking-wider uppercase">
                          {pkg.categoryEn}
                        </p>
                        <p className="text-gray-600 text-xs mt-1">
                          {pkg.description}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {pkg.items.map((item, itemIndex) => (
                        <motion.div
                          key={itemIndex}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: pkgIndex * 0.1 + itemIndex * 0.05, duration: 0.5 }}
                          className={`relative bg-white/5 border ${
                            item.popular ? 'border-amber-500/40' : 'border-white/10'
                          } rounded-2xl p-6 backdrop-blur-md hover:bg-white/10 transition-all duration-300 ${
                            item.popular ? 'md:scale-105' : ''
                          }`}
                        >
                          {item.badge && (
                            <div className="absolute -top-2 -right-2 bg-amber-500 text-black text-[9px] px-3 py-1 rounded-full">
                              {item.badge}
                            </div>
                          )}

                          <div className="text-center mb-6">
                            <div className="text-4xl font-light text-white mb-2">
                              {item.amount}
                              <span className="text-lg text-gray-500 ml-1">張</span>
                            </div>
                            <div className="text-2xl text-amber-400">${item.price}</div>
                            <p className="text-gray-600 text-xs mt-2">
                              平均 ${item.perUnit.toFixed(3)} / 張
                            </p>
                          </div>

                          <button
                            onClick={() => handleBuyCredits(pkg.category, item.amount, item.price)}
                            className={`w-full py-3 rounded-xl text-sm tracking-widest transition-all duration-300 ${
                              item.popular
                                ? 'bg-amber-500 text-black hover:bg-amber-400'
                                : 'bg-white/10 text-amber-400 border border-amber-500/30 hover:bg-amber-500/10'
                            }`}
                          >
                            立即購買
                          </button>

                          <div className="mt-4 pt-4 border-t border-white/5">
                            <p className="text-amber-400/90 text-xs text-center tracking-widest font-medium">
                              ✨ 永不過期 · 隨時使用
                            </p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-20 text-center">
            <p className="text-gray-400 text-sm tracking-wider mb-4">
              有任何疑問? 查看我們的{' '}
              <button
                onClick={() => navigate('/terms')}
                className="text-amber-400 hover:text-amber-300 underline font-medium"
              >
                服務條款
              </button>{' '}
              或{' '}
              <a href="mailto:info@homy.hk" className="text-amber-400 hover:text-amber-300 underline font-medium">
                聯繫我們
              </a>
            </p>
            <div className="mt-6 p-4 bg-amber-500/5 border border-amber-500/20 rounded-xl backdrop-blur-sm">
              <p className="text-amber-300/90 text-sm tracking-wider font-medium">
                💳 所有價格以美元 (USD) 計價
              </p>
              <p className="text-amber-300/80 text-xs tracking-wider mt-2">
                訂閱可隨時取消 · 無隱藏費用
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}